import type { GameEvent } from "../core/types";

export class AudioEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambient: OscillatorNode | null = null;
  private enabled = true;

  isEnabled(): boolean {
    return this.enabled;
  }

  async start(): Promise<void> {
    if (!this.enabled) return;
    this.ensureContext();
    await this.context?.resume();
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    if (this.master) {
      this.master.gain.setTargetAtTime(
        this.enabled ? 0.22 : 0,
        this.context?.currentTime ?? 0,
        0.04,
      );
    }
    if (this.enabled) void this.start();
    return this.enabled;
  }

  handle(events: GameEvent[]): void {
    if (!this.enabled || !this.context || !this.master) return;
    for (const event of events) {
      if (event.type === "sonar_pulse") this.tone(240, 840, 0.55, "sine", 0.2);
      if (event.type === "system_routed") this.tone(420, 520, 0.08, "square", 0.06);
      if (event.type === "weapon_fired") {
        const surge = Boolean(event.data?.overdrive);
        this.tone(surge ? 640 : 310, surge ? 180 : 220, surge ? 0.055 : 0.04, "square", surge ? 0.045 : 0.025);
      }
      if (event.type === "enemy_hit") {
        this.tone(Boolean(event.data?.surge) ? 880 : 620, 260, 0.045, "sawtooth", 0.035);
      }
      if (event.type === "enemy_destroyed") this.tone(160, 70, 0.18, "sawtooth", 0.09);
      if (event.type === "combo_changed") {
        const combo = Math.max(1, Number(event.data?.combo ?? 1));
        this.tone(420 + Math.min(8, combo) * 55, 680 + Math.min(8, combo) * 70, 0.1, "sine", 0.055);
      }
      if (event.type === "player_damaged") this.tone(90, 45, 0.22, "square", 0.1);
      if (event.type === "pickup_collected") this.tone(520, 980, 0.16, "sine", 0.08);
      if (event.type === "overdrive_started") {
        this.chord([220, 440, 659.25, 880], 0.42);
      }
      if (event.type === "overdrive_ended") this.tone(460, 180, 0.28, "sine", 0.055);
      if (event.type === "boss_spawned") this.tone(65, 34, 1.1, "sawtooth", 0.16);
      if (event.type === "run_won") this.chord([392, 523.25, 659.25], 0.8);
      if (event.type === "run_lost") this.chord([146.83, 138.59, 110], 0.9);
    }
  }

  private ensureContext(): void {
    if (this.context) return;
    this.context = new AudioContext();
    this.master = this.context.createGain();
    this.master.gain.value = this.enabled ? 0.22 : 0;
    this.master.connect(this.context.destination);

    const ambientGain = this.context.createGain();
    ambientGain.gain.value = 0.035;
    ambientGain.connect(this.master);
    this.ambient = this.context.createOscillator();
    this.ambient.type = "sine";
    this.ambient.frequency.value = 42;
    this.ambient.connect(ambientGain);
    this.ambient.start();
  }

  private tone(
    from: number,
    to: number,
    duration: number,
    type: OscillatorType,
    volume: number,
  ): void {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, to), now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  private chord(frequencies: number[], duration: number): void {
    frequencies.forEach((frequency, index) => {
      window.setTimeout(
        () => this.tone(frequency, frequency * 1.02, duration, "sine", 0.075),
        index * 100,
      );
    });
  }
}
